import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { PowerEfficiencyFlagFragmentFragmentDoc } from '@packages/gql/generated/shopping/PowerEfficiencyFlagFragmentFragmentDoc';
import { EfficiencyFlagOverlayDialog } from '@packages/shared/src/ui/EfficiencyFlagOverlayDialog/EfficiencyFlagOverlayDialog';
import type { EnergyEfficiencyFlagSpecificProps } from '@packages/shared/src/ui/EnergyEfficiencyFlag/EnergyEfficiencyFlag';
import { getValidColorAndEfficiencyClass } from '@packages/shared/src/ui/EnergyEfficiencyFlag/colorsForEnergyEfficiency';
import { LegacyEnergyEfficiencyFlag } from '@packages/shared/src/ui/EnergyEfficiencyFlag/LegacyEnergyEfficiencyFlag/LegacyEnergyEfficiencyFlag';
import { NewEnergyEfficiencyFlag } from '@packages/shared/src/ui/EnergyEfficiencyFlag/NewEnergyEfficiencyFlag/NewEnergyEfficiencyFlag';
import { useIntl } from 'react-intl';
import { useLayerTracking } from '../../Tracking/hooks/useLayerTracking/useLayerTracking';
import { powerEfficiencyFlagMessages } from './messages';

/* GraphQL */ `
  fragment PowerEfficiencyFlagFragment on PowerEfficiencyFlag {
    color
    level
    link
    text
    hasOldLabel
  }
`;

export type PowerEfficiencyFlagProps = {
  data: FragmentType<typeof PowerEfficiencyFlagFragmentFragmentDoc>;
  showLabel?: boolean;
} & Pick<EnergyEfficiencyFlagSpecificProps, 'size'>;

export const PowerEfficiencyFlag = ({
  data,
  showLabel = false,
  size = 'large',
}: PowerEfficiencyFlagProps) => {
  const { color, hasOldLabel, level, link, text } = getFragmentData(
    PowerEfficiencyFlagFragmentFragmentDoc,
    data,
  );

  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();

  const handleOnDialogOpen = () => {
    trackViewLayer('EEK Label(powerEfficiencyFlag)');
  };

  const colorAndEfficiencyClass = getValidColorAndEfficiencyClass(color);

  if (colorAndEfficiencyClass === undefined) {
    return null;
  }

  const flag = hasOldLabel ? (
    <LegacyEnergyEfficiencyFlag
      text={level}
      color={colorAndEfficiencyClass.color}
      size={size}
      aria-label={formatMessage(powerEfficiencyFlagMessages.ariaLabel, {
        energyClass: level,
      })}
    />
  ) : (
    <NewEnergyEfficiencyFlag
      efficiencyClass={colorAndEfficiencyClass.efficiencyClass}
      size={size}
      aria-label={formatMessage(powerEfficiencyFlagMessages.ariaLabel, {
        energyClass: colorAndEfficiencyClass.efficiencyClass,
      })}
    />
  );

  return (
    <EfficiencyFlagOverlayDialog
      overlay={{
        title: text,
        imgUrl: link,
      }}
      onDialogOpen={handleOnDialogOpen}
    >
      {showLabel ? (
        <span className="flex flex-row flex-nowrap items-center gap-1">
          <span>{flag}</span>
          <span>{text}</span>
        </span>
      ) : (
        flag
      )}
    </EfficiencyFlagOverlayDialog>
  );
};
